import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import {
  CustomCell,
  GridCellKind,
  ProvideEditorCallback,
  Theme,
  type CustomRenderer,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { URLCell } from '@/core/models/columns/url'

/**
 * Interfaces for custom URL cell properties, text editor props, and icon properties
 */
interface CustomUrlCellProps {
  readonly type: ColumnTypes
  readonly cell: URLCell
  readonly extra: {
    readonly theme: string
  }
}

interface TextEditorProps {
  value?: string
  onChange: (nextValue: string) => void
  themeMode?: string
  theme?: Theme
}

interface IconProperties {
  symbol: string
  xPosition: number
  width: number
  action: string
  color?: string
  preventActivation: boolean
}

// Constants
const MAX_URL_DISPLAY_LENGTH = 50
const ICON_FONT_STYLE = '20px Material Symbols Rounded'

export type CustomUrlCell = CustomCell<CustomUrlCellProps>

/**
 * CustomUrlRenderer: A custom renderer for URL cells in a data grid.
 * It handles the display, interaction, and editing of URL cells.
 */
const CustomUrlRenderer: CustomRenderer<CustomUrlCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomUrlCell => (cell.data as CustomUrlCellProps).type === ColumnTypes.URL,
  measure: (ctx, cell: any, theme) => {
    const url = cell.data.cell.toString()
    const truncatedUrl = truncateUrlDisplay(url)
    return ctx.measureText(truncatedUrl).width + theme.cellHorizontalPadding * 2 + 80
  },
  needsHover: true,
  draw: (args) => {
    const { cell, theme, rect, ctx, hoverAmount } = args
    const url = cell.data.cell.toString()
    const truncatedUrl = truncateUrlDisplay(url)
    const isLinky = true

    const isEmptyUrl = !truncatedUrl || truncatedUrl.trim() === ''

    ctx.font = theme.baseFontFull
    const textMetrics = ctx.measureText(truncatedUrl)
    const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent

    const verticalCenter = rect.y + rect.height / 2
    const textY = verticalCenter + textHeight / 2 - textMetrics.actualBoundingBoxDescent

    // Draw URL
    ctx.fillStyle = isLinky ? theme.linkColor : theme.textDark
    ctx.fillText(truncatedUrl, rect.x + theme.cellHorizontalPadding, textY)

    // Draw underline for URL
    if (!isEmptyUrl) {
      const underlineY = Math.floor(textY + textMetrics.actualBoundingBoxDescent + 1) + 0.5
      const startX = rect.x + theme.cellHorizontalPadding
      const endX = startX + textMetrics.width

      ctx.beginPath()
      ctx.moveTo(startX, underlineY)
      ctx.lineTo(endX, underlineY)
      ctx.strokeStyle = ctx.fillStyle // Use the same color as the text
      ctx.lineWidth = 1
      ctx.stroke()
    }

    // Calculate URL width
    const urlWidth = textMetrics.width

    if (!isEmptyUrl && hoverAmount > 0) {
      const iconX = rect.x + theme.cellHorizontalPadding + urlWidth + 10 // Start icons after URL

      ctx.font = ICON_FONT_STYLE
      const iconMetrics = ctx.measureText('M')
      const iconHeight = iconMetrics.actualBoundingBoxAscent + iconMetrics.actualBoundingBoxDescent
      const iconY = verticalCenter + iconHeight / 2 - iconMetrics.actualBoundingBoxDescent

      const icons: IconProperties[] = [
        {
          symbol: 'content_copy',
          xPosition: iconX,
          width: 24,
          action: 'copy',
          color: theme.textDark,
          preventActivation: true,
        },
        {
          symbol: 'edit',
          xPosition: iconX + 30,
          width: 24,
          action: 'edit',
          color: theme.textDark,
          preventActivation: false,
        },
        {
          symbol: 'delete',
          xPosition: iconX + 60,
          width: 24,
          action: 'delete',
          color: theme.textDark,
          preventActivation: true,
        },
      ]
      renderActionIcons(ctx, icons, iconY)
    }
  },

  provideEditor: () => {
    return Editor
  },
  onClick: (args: any): CustomUrlCell | undefined => {
    const urlCell = args.cell.data.cell
    const { action, preventActivation } = handleCellClick(args)

    if (preventActivation) {
      args.preventDefault()
    }

    switch (action) {
      case 'open_editor':
        // Implementation for opening editor directly
        return undefined
      case 'open_url':
        window.open(urlCell.toString(), '_blank')
        break
      case 'copy':
        navigator.clipboard
          .writeText(urlCell.toString())
          .then(() => toast.success('URL copied to clipboard'))
          .catch((err) => console.error('Failed to copy URL: ', err))
        break
      case 'delete':
        const dataEditorRef = args.cell.data.extra.dataEditorRef.current
        urlCell.toCell('')
        dataEditorRef.emit('delete')
        break
      default:
        // If no special action, allow default behavior (cell activation)
        return undefined
    }

    // If we reach here, we've handled the action
    return {
      ...args.cell,
      data: {
        ...args.cell.data,
        cell: urlCell,
      },
    } as CustomUrlCell
  },

  onDelete: (args) => {
    const urlCell = args.data.cell
    urlCell.toCell('')
    toast.error('URL deleted')
    return {
      ...args,
      data: {
        ...args.data,
        cell: urlCell,
      },
    }
  },
  needsHoverPosition: true,
}

/**
 * TextEditor: A component for editing text in a cell.
 * It provides an input field for modifying the cell's content.
 */
const TextEditor = ({ value, onChange, themeMode, theme }: TextEditorProps) => {
  const [textValue, setTextValue] = useState(value || '')

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTextValue(e.target.value)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (textValue !== value) {
          onChange(textValue)
        }
      }
    },
    [onChange, textValue, value]
  )

  return (
    <input
      type='text'
      value={textValue}
      className='w-full pt-1'
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      autoFocus
      style={{
        color: themeMode === 'dark' ? theme?.textDark : theme?.textDark,
        backgroundColor: theme?.bgCell,
      }}
    />
  )
}

/**
 * Editor: The main editor component for URL cells.
 * It handles the editing process and validation of URLs.
 */
const Editor: ReturnType<ProvideEditorCallback<CustomUrlCell>> = (args) => {
  const cell = args.value.data.cell
  const cellValue = cell.toString()
  const themeMode = args.value.data.extra.theme

  return (
    <TextEditor
      value={cellValue}
      themeMode={themeMode}
      theme={args.theme}
      onChange={(textValue) => {
        // Check if newValue is a valid URL using regex, must also be valid for IP addresses, localhost, and include port
        const regexUrl =
          /^(https?:\/\/)?(localhost|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|([\da-z\.-]+)\.([a-z\.]{2,6}))(:([0-9]{1,5}))?(\/.*)?$/i
        let newURL = textValue

        const isValidUrl = regexUrl.test(newURL)

        if (!isValidUrl) {
          toast.error('Invalid URL')
          args.onFinishedEditing()
          return
        }

        // Add https:// if no protocol is present
        if (!/^https?:\/\//i.test(newURL)) {
          newURL = 'http://' + newURL
        }

        const updatedCell = cell.toCell(newURL)
        args.onFinishedEditing({
          ...args.value,
          data: {
            ...args.value.data,
            cell: updatedCell,
          },
          copyData: updatedCell.toString(),
        } as CustomUrlCell)
      }}
    />
  )
}

/**
 * handleCellClick: Determines which action to take based on where the user clicked in the cell.
 * Returns the action to be performed (e.g., 'openUrl', 'copyUrl', 'editUrl', 'deleteUrl').
 */
const handleCellClick = (args: any): { action: string; preventActivation: boolean } => {
  const { cell, posX: clickX, bounds: rect, theme } = args
  const url = cell.data.cell.toString()
  const isEmptyUrl = !url || url.trim() === ''

  if (isEmptyUrl) {
    return { action: 'open_editor', preventActivation: false }
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) return { action: '', preventActivation: false }

  ctx.font = theme.baseFontFull
  const urlX = rect.x + theme.cellHorizontalPadding
  const urlWidth = ctx.measureText(truncateUrlDisplay(url)).width

  const clickArea = urlX + urlWidth

  if (rect.x + clickX <= clickArea) {
    return { action: 'open_url', preventActivation: true }
  }

  const icons: IconProperties[] = [
    { symbol: 'content_copy', xPosition: urlX + urlWidth + 10, width: 24, action: 'copy', preventActivation: true },
    { symbol: 'edit', xPosition: urlX + urlWidth + 40, width: 24, action: 'open_editor', preventActivation: false },
    { symbol: 'delete', xPosition: urlX + urlWidth + 70, width: 24, action: 'delete', preventActivation: true },
  ]

  const rectClickX = rect.x + clickX
  for (const icon of icons) {
    if (rectClickX >= icon.xPosition && rectClickX < icon.xPosition + icon.width) {
      return { action: icon.action, preventActivation: icon.preventActivation }
    }
  }

  return { action: '', preventActivation: false }
}

/**
 * truncateUrlDisplay: Shortens a URL for display if it exceeds MAX_URL_DISPLAY_LENGTH by
 * adding an ellipsis (...) at the end.
 */
const truncateUrlDisplay = (url: string): string => {
  return url.length <= MAX_URL_DISPLAY_LENGTH ? url : `${url.slice(0, MAX_URL_DISPLAY_LENGTH - 3)}...`
}

/**
 * renderActionIcons: Renders action icons (open, copy, edit, delete) when hovering over a non-empty cell.
 */
const renderActionIcons = (ctx: CanvasRenderingContext2D, icons: IconProperties[], yPosition: number) => {
  ctx.font = ICON_FONT_STYLE
  icons.forEach((icon) => {
    if (icon.color) {
      ctx.fillStyle = icon.color
    }
    ctx.fillText(icon.symbol, icon.xPosition, yPosition)
  })
}

export default CustomUrlRenderer
